import axios from 'axios';

// Base API URL
// const API_URL = process.env.REACT_APP_API_URL || 'http://ec2-3-83-124-144.compute-1.amazonaws.com/api';
const API_URL = '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add authorization header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 Unauthorized and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          // No refresh token, force login
          localStorage.removeItem('token');
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken
        });
        
        // Update tokens
        const { token, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        
        // Retry original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, force login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const uploadFile = async (url, formData) => {
  try {
    const token = localStorage.getItem('token');
    
    const config = {
      baseURL: API_URL,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    // Add authorization header if token exists
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await axios.post(`${url}`, formData, config);
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

// Helper to extract data from response
const extractData = (response) => response.data;

// Helper to handle errors
const handleError = (error) => {
  if (error.response) {
    // Server responded with non 2xx status
    const message = error.response.data.message || error.response.data.detail || 'An error occurred';
    throw new Error(message);
  } else if (error.request) {
    // Request made but no response
    throw new Error('No response from server. Please check your connection.');
  } else {
    // Something else happened
    throw error;
  }
};

// HTTP methods
export const get = (url, params = {}) => {
  return api.get(url, { params })
    .then(extractData)
    .catch(handleError);
};

export const post = (url, data = {}) => {
  return api.post(url, data)
    .then(extractData)
    .catch(handleError);
};

export const put = (url, data = {}) => {
  return api.put(url, data)
    .then(extractData)
    .catch(handleError);
};

export const patch = (url, data = {}) => {
  return api.patch(url, data)
    .then(extractData)
    .catch(handleError);
};

export const del = (url) => {
  return api.delete(url)
    .then(extractData)
    .catch(handleError);
};

export default {
  get,
  post,
  put,
  patch,
  delete: del,
  uploadFile,
  API_URL
};